import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { getAllCompounds, getTodoCompounds, getStats, categories } from '../data/compounds';
import './Journal.css';

export default function Journal() {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const todoCompounds = useMemo(() => getTodoCompounds(), []);
  const all = useMemo(() => getAllCompounds(), []);
  const stats = useMemo(() => getStats(), []);

  const shown = useMemo(() => {
    let list = todoCompounds;
    if (filter !== 'all') {
      list = list.filter(c => {
        if (filter === 'low-evidence') return c.evidenceConfidence === 'LOW' || c.wohlerScoring?.evidenceConfidence === 'LOW';
        if (filter === 'hold') return c.status === 'HOLD' || c.status === 'Hold';
        if (filter === 'has-next-steps') return c.nextSteps && c.nextSteps.length > 0;
        if (filter === 'no-synthesis') return !c.labSynthesis && !c.synthesisRoute;
        if (filter === 'no-kg') return !c.kgScaleSynthesis;
        return true;
      });
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.fullName?.toLowerCase().includes(q) ||
        c.id.toLowerCase().includes(q)
      );
    }
    return list;
  }, [todoCompounds, filter, search]);

  // Compounds missing kg-scale synthesis
  const missingKg = all.filter(c => !c.kgScaleSynthesis).length;
  const missingLab = all.filter(c => !c.labSynthesis && !c.synthesisRoute).length;
  const lowEvidence = all.filter(c => c.evidenceConfidence === 'LOW' || c.wohlerScoring?.evidenceConfidence === 'LOW').length;

  return (
    <div className="jn-page">
      <div className="jn-header">
        <div className="jn-header-inner">
          <div className="jn-bc">
            <Link to="/">Home</Link> <span>›</span> <span>Research Journal</span>
          </div>
          <h1 className="jn-title">📓 Research Journal & To-Do</h1>
          <p className="jn-subtitle">
            Active research tasks, compounds pending further scoring, and open questions across the Wohler pipeline.
            This page consolidates every compound flagged for additional work.
          </p>

          <div className="jn-stats">
            <div className="jn-stat">
              <div className="jn-stat-num">{stats.total}</div>
              <div className="jn-stat-label">Total Compounds</div>
            </div>
            <div className="jn-stat">
              <div className="jn-stat-num" style={{color:'#10b981'}}>{stats.byStatus.go}</div>
              <div className="jn-stat-label">GO / Active</div>
            </div>
            <div className="jn-stat">
              <div className="jn-stat-num" style={{color:'#f59e0b'}}>{stats.byStatus.hold}</div>
              <div className="jn-stat-label">HOLD</div>
            </div>
            <div className="jn-stat">
              <div className="jn-stat-num" style={{color:'#ef4444'}}>{stats.byStatus.kill}</div>
              <div className="jn-stat-label">KILL / Watch-only</div>
            </div>
            <div className="jn-stat">
              <div className="jn-stat-num" style={{color:'#6366f1'}}>{todoCompounds.length}</div>
              <div className="jn-stat-label">Open Tasks</div>
            </div>
          </div>

          <div className="jn-gap-row">
            <div className="jn-gap">
              <strong>⚗️ Missing Lab Synthesis:</strong> {missingLab} compound{missingLab !== 1 ? 's' : ''}
            </div>
            <div className="jn-gap">
              <strong>🏭 Missing Kg-Scale Plan:</strong> {missingKg} compound{missingKg !== 1 ? 's' : ''}
            </div>
            <div className="jn-gap">
              <strong>📉 Low-Evidence Scoring:</strong> {lowEvidence} compound{lowEvidence !== 1 ? 's' : ''}
            </div>
          </div>
        </div>
      </div>

      <div className="jn-filters">
        <div className="jn-filters-inner">
          <input
            className="jn-search"
            type="text"
            placeholder="🔍 Search compounds by name or ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="jn-select" value={filter} onChange={e => setFilter(e.target.value)}>
            <option value="all">All To-Do Compounds</option>
            <option value="has-next-steps">Has Open Next Steps</option>
            <option value="low-evidence">Low Evidence Confidence</option>
            <option value="hold">Status: HOLD</option>
            <option value="no-synthesis">Missing Lab Synthesis</option>
            <option value="no-kg">Missing Kg-Scale Plan</option>
          </select>
          <div className="jn-count">{shown.length} compound{shown.length !== 1 ? 's' : ''}</div>
        </div>
      </div>

      <div className="jn-content">
        <div className="jn-content-inner">
          {shown.length === 0 ? (
            <div className="jn-empty">
              <div style={{fontSize: 48, marginBottom: 12}}>✅</div>
              <h3>No compounds match this filter</h3>
              <p>Try adjusting filters or search.</p>
            </div>
          ) : (
            <div className="jn-list">
              {shown.map(c => {
                const cat = categories.find(x => x.id === c.categoryId);
                const tasks = c.nextSteps || [];
                return (
                  <Link key={c.id} to={`/compounds/${c.id}`} className="jn-card">
                    <div className="jn-card-head">
                      <div>
                        <div className="jn-card-name">{c.name}</div>
                        <div className="jn-card-full">{c.fullName}</div>
                      </div>
                      <div className="jn-card-badges">
                        {cat && <span className="jn-card-cat" style={{color: cat.color, borderColor: cat.color + '40', background: cat.color + '10'}}>{cat.icon} {cat.name}</span>}
                        {c.status && <span className="jn-card-status">{c.status}</span>}
                      </div>
                    </div>
                    {tasks.length > 0 && (
                      <ul className="jn-card-tasks">
                        {tasks.slice(0, 3).map((t, i) => (
                          <li key={i}>☐ {typeof t === 'string' ? t : (t.task || '')}</li>
                        ))}
                        {tasks.length > 3 && <li className="jn-more">+ {tasks.length - 3} more tasks…</li>}
                      </ul>
                    )}
                    <div className="jn-card-meta">
                      {c.benefitScore !== undefined && <span>Benefit: {c.benefitScore}</span>}
                      {c.harmScore !== undefined && <span>Harm: {c.harmScore}</span>}
                      {c.evidenceConfidence && <span>Evidence: {c.evidenceConfidence}</span>}
                      {!c.kgScaleSynthesis && <span className="jn-warn">⚠ No kg-plan</span>}
                      {!c.labSynthesis && !c.synthesisRoute && <span className="jn-warn">⚠ No lab route</span>}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}